import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import i18n from "@/i18n.ts";
import { useSourceSave } from "@/queries/use-sources.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiSource, ApiSourceSavePayload } from "@/types/recruit/source.types.ts";
import { Button, Input, useModals, Modal, ModalProps, Stack } from "@skailer-com/uikit";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

export interface SourceEditModalProps extends ModalProps {
  source: ApiSource;
}

export function SourceEditModal({ source, ...props }: SourceEditModalProps) {
  const { close } = useModals();
  const { id } = { ...props };
  const { t } = useTranslation();

  const SourceSchemaObject = z.object({
    title: z
      .string({ error: i18n.t("errors:field_required") })
      .min(1, i18n.t("errors:field_required")),
  });

  type SourceSchema = z.infer<typeof SourceSchemaObject>;

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<SourceSchema>({
    mode: "onSubmit",
    resolver: zodResolver(SourceSchemaObject),
    defaultValues: {
      title: source.title,
    },
  });

  const sourceSave = useSourceSave(control);

  const onSubmit = handleSubmit(async (sourceData) => {
    const payload: ApiSourceSavePayload = {
      title: sourceData.title,
    };

    sourceSave.mutate(
      { id: source.id, payload },
      {
        onSuccess: (data) => {
          if (source.id == 0) {
            analyticsService.trackEvent(analyticEvents.sources.created, {
              [analyticProperties.id]: data.id,
            });
          } else {
            analyticsService.trackEvent(analyticEvents.sources.edited, {
              [analyticProperties.id]: source.id,
            });
          }
          close(id);
        },
      }
    );
  });

  return (
    <Modal
      {...props}
      title={t("common:source")}
      withCloser
      actions={
        <Stack gap={"sm"}>
          <ServerErrorField errors={errors} />
          <Button type={"submit"} size={"lg"} isLoading={sourceSave.isPending} onClick={onSubmit}>
            {t("common:save")}
          </Button>
        </Stack>
      }
    >
      <form onSubmit={onSubmit}>
        <Input
          label={t("common:title")}
          required={true}
          autoFocus={true}
          type="text"
          {...register("title")}
          error={errors.title?.message}
        />
      </form>
    </Modal>
  );
}
